/**
 * Transform async — 폴링 훅.
 *
 * POST /api/transform 으로 jobId 받고 1.5s 마다 GET /api/transform/:jobId 폴링.
 * done 시 resultImageUrl 을 fetch → blob → dataUrl 변환해 기존 키오스크 흐름과 호환.
 *
 * AbortController 로 stage 이탈 시 폴링 + 진행 중 fetch 모두 취소.
 *
 * 설계: docs/booth/transform-async-design.md
 */
import { useEffect, useRef, useState, type MutableRefObject } from "react";
import type {
  JobPhase,
  JobStatus,
  JobStatusResponse,
  TransformCreateResponse,
} from "@fiket/booth-schema";
import { fitDataUrlToPixelsPair } from "../../utils/fitImage";
import type { WatermarkStyle } from "@fiket/booth-schema";

/** 워터마크 이미지 + 스타일 쌍 — 같은 config 스냅숏에서 와야 PNG 띠 높이와 overlay 기하가 맞는다 (BOOTH-129). */
type WatermarkOverlay = { img: HTMLImageElement | null; style: WatermarkStyle | undefined };
import { useBoothConfig } from "../../store/booth";
import { useRouter } from "../../store/router";
import { trackError } from "../../telemetry/activity";
import { ApiEnvelopeError } from "../../lib/apiEnvelope";

const POLL_INTERVAL_MS = 1500;

/** 결과를 맞출 인쇄 프리셋 픽셀 (BOOTH-35). 호출측이 useMemo 로 안정 참조를 넘긴다. */
export interface FitTarget {
  w: number;
  h: number;
  dpi?: number;
}

interface UseTransformJobInput {
  /** dataURL 형식 입력 이미지. null 이면 idle. */
  image: string | null;
  styleId: string | null;
  /** 있으면 결과 수신 직후 이 픽셀로 중앙 크롭+리사이즈(JPEG). 없으면 Gemini 결과 그대로. */
  fitTo?: FitTarget | null;
  /**
   * 워터마크 이미지 (BOOTH-103). 있으면 크롭·리사이즈 후 하단에 얹어 `branded` 사본을 만든다.
   * job 재시작 방지를 위해 ref 로 관리 — 결과 수신 시점의 값을 사용한다.
   * 워터마크가 늦게 로드돼도 이미 완료된 job 을 재시작하지 않는다.
   */
  watermark?: HTMLImageElement | null;
  /** 워터마크 위치·크기 (BOOTH-129) — 이미지와 같은 config 스냅숏에서 온 값. ref 로 함께 전달. */
  watermarkStyle?: WatermarkStyle;
}

interface ApiOk<T> {
  success: true;
  data: T;
}
interface ApiFail {
  success: false;
  error: { code: string; message: string };
}

export interface TransformJobState {
  status: JobStatus | "idle";
  phase: JobPhase | null;
  attempt: number;
  resultDataUrl: string | null;
  /** 워터마크 없는 변환 결과 사본 (BOOTH-103) — 영상 생성 입력. `resultDataUrl` 과 같거나 워터마크 전 `clean`. */
  videoInputDataUrl: string | null;
  errorMessage: string | null;
}

const INITIAL: TransformJobState = {
  status: "idle",
  phase: null,
  attempt: 0,
  resultDataUrl: null,
  videoInputDataUrl: null,
  errorMessage: null,
};

export interface UseTransformJobResult extends TransformJobState {
  /** 같은 image/styleId 로 새 job 을 강제로 시작 (retry 버튼 등). */
  restart: () => void;
}

export function useTransformJob({
  image,
  styleId,
  fitTo = null,
  watermark = null,
  watermarkStyle,
}: UseTransformJobInput): UseTransformJobResult {
  const [state, setState] = useState<TransformJobState>(INITIAL);
  const [nonce, setNonce] = useState(0);
  // image identity 가 바뀌면 새 job 시작. styleId 변경도 마찬가지.
  const startedKeyRef = useRef<string | null>(null);
  const fitKey = fitTo ? `${fitTo.w}x${fitTo.h}@${fitTo.dpi ?? 0}` : "";
  /**
   * 워터마크를 ref 로 관리 — fitKey 에 포함하지 않아 이미지 로드 전후로 job 이 재시작되지 않는다.
   * 결과 수신 시점의 최신 값을 읽어 합성한다. 늦게 로드돼도 완료된 job 을 재시작하지 않는 것이 의도된 동작.
   */
  const watermarkRef = useRef<WatermarkOverlay>({ img: watermark, style: watermarkStyle });
  useEffect(() => {
    watermarkRef.current = { img: watermark, style: watermarkStyle };
  });

  useEffect(() => {
    if (!image || !styleId) {
      setState(INITIAL);
      startedKeyRef.current = null;
      return;
    }
    const key = `${styleId}::${image.slice(-32)}::${fitKey}::${nonce}`;
    if (startedKeyRef.current === key) return;
    startedKeyRef.current = key;

    setState({ ...INITIAL, status: "queued" });

    const controller = new AbortController();
    void runJob({ image, styleId, fitTo, watermarkRef, signal: controller.signal, setState });

    return () => {
      controller.abort();
    };
    // fitTo 는 fitKey 로 비교 (객체 identity 변화로 job 재시작 방지)
    // watermark 는 ref 로 전달 — fitKey 에 포함하지 않아 job 재시작 없음
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [image, styleId, fitKey, nonce]);

  return {
    ...state,
    restart: () => setNonce((n) => n + 1),
  };
}

async function runJob({
  image,
  styleId,
  fitTo,
  watermarkRef,
  signal,
  setState,
}: {
  image: string;
  styleId: string;
  fitTo: FitTarget | null;
  watermarkRef: MutableRefObject<WatermarkOverlay>;
  signal: AbortSignal;
  setState: (updater: (prev: TransformJobState) => TransformJobState) => void;
}): Promise<void> {
  try {
    const jobId = await createJob({ image, styleId, signal });
    if (signal.aborted) return;

    setState((prev) => ({ ...prev, status: "queued", phase: "enqueued" }));

    while (!signal.aborted) {
      await sleep(POLL_INTERVAL_MS, signal);
      if (signal.aborted) return;

      const status = await pollJob(jobId, signal);
      if (signal.aborted) return;

      setState((prev) => ({
        ...prev,
        status: status.status,
        phase: status.phase,
        attempt: status.attempt,
        errorMessage: status.errorMessage ?? null,
      }));

      if (status.status === "done") {
        if (!status.resultImageUrl) {
          setState((prev) => ({
            ...prev,
            status: "error",
            errorMessage: "결과 URL 없음",
          }));
          return;
        }
        const raw = await fetchAsDataUrl(status.resultImageUrl, signal);
        if (signal.aborted) return;
        // 인쇄 프리셋 픽셀로 최종 맞춤 + 워터마크 합성 (BOOTH-35, BOOTH-103).
        // watermarkRef.current: 결과 수신 시점의 최신 워터마크 이미지를 사용.
        // clean: 워터마크 없는 사본(영상 생성 입력), branded: 워터마크 있는 사본(리뷰·업로드·공유).
        const pair = fitTo
          ? await fitDataUrlToPixelsPair(raw, fitTo, { dpi: fitTo.dpi, overlay: watermarkRef.current.img, overlayStyle: watermarkRef.current.style })
          : { clean: raw, branded: raw };
        if (signal.aborted) return;
        setState((prev) => ({
          ...prev,
          resultDataUrl: pair.branded,
          videoInputDataUrl: pair.clean,
        }));
        return;
      }

      if (status.status === "error") {
        // 서버 워커가 transform_error 를 따로 남기지만, 손님이 본 실패도 키오스크 쪽 사건으로 남긴다 (BOOTH-109).
        trackError("transform_failed", undefined, {
          message: status.errorMessage ?? status.errorCode ?? "transform error",
          serverCode: status.errorCode,
        });
        return;
      }
    }
  } catch (err) {
    if (signal.aborted) return;
    trackError("transform_failed", err);
    setState((prev) => ({
      ...prev,
      status: "error",
      errorMessage: (err as Error).message,
    }));
  }
}

async function createJob({
  image,
  styleId,
  signal,
}: {
  image: string;
  styleId: string;
  signal: AbortSignal;
}): Promise<string> {
  // 통계 축 (BOOTH-87) — worker 가 transform_success/error 를 부스별 활동 이벤트로 남길 수 있게 부스·방문 id 를 싣는다
  const boothId = useBoothConfig.getState().config?.booth.id;
  const visitId = useRouter.getState().visitId;
  const resp = await fetch("/api/transform", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ image, styleId, ...(boothId ? { boothId } : {}), ...(visitId ? { visitId } : {}) }),
    signal,
  });
  const body = (await resp.json()) as
    | ApiOk<TransformCreateResponse>
    | ApiFail;
  if (!body.success) {
    // code·httpStatus 를 보존해 trackError 가 detail 로 풀 수 있게 (BOOTH-109)
    throw new ApiEnvelopeError(body.error.code ?? "TRANSFORM_CREATE_FAILED", body.error.message ?? body.error.code ?? "job 생성 실패", resp.status);
  }
  return body.data.jobId;
}

async function pollJob(
  jobId: string,
  signal: AbortSignal
): Promise<JobStatusResponse> {
  const resp = await fetch(`/api/transform/${jobId}`, { signal });
  const body = (await resp.json()) as ApiOk<JobStatusResponse> | ApiFail;
  if (!body.success) {
    throw new ApiEnvelopeError(body.error.code ?? "TRANSFORM_POLL_FAILED", body.error.message ?? body.error.code ?? "job 조회 실패", resp.status);
  }
  return body.data;
}

async function fetchAsDataUrl(
  url: string,
  signal: AbortSignal
): Promise<string> {
  const resp = await fetch(url, { signal });
  if (!resp.ok) throw new Error(`결과 다운로드 실패 (HTTP ${resp.status})`);
  const blob = await resp.blob();
  return await blobToDataUrl(blob);
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error("FileReader 실패"));
    reader.readAsDataURL(blob);
  });
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => resolve(), ms);
    signal.addEventListener(
      "abort",
      () => {
        clearTimeout(t);
        reject(new DOMException("aborted", "AbortError"));
      },
      { once: true }
    );
  });
}
